import axios from 'axios';
import { API_URL } from './config';
import { uploadFile } from 'utils/files';

export interface ResourcePost {
  subject: string;
  level: string;
  type: string;
  revisionType?: string;
  title: string;
  link?: string;
  fileType: 'URL' | 'FILE';
  resourceS3Key?: string;
  file: File | null;
}

export interface IResource {
  id: number;
  subject: string;
  level: string;
  type: string;
  revisionType?: string;
  title: string;
  link: string;
  resourceS3Key: string;
  fileType: string;
  slug: string;
  isEnabled?: boolean;
}

export interface IResourcePage {
  rows: IResource[];
  total: number;
  page: number;
  pageSize: number;
}

export const ResourcesApi = {
  add: async function (data: ResourcePost) {
    let { file, ...resourceData } = data;

    if (data.fileType === 'FILE' && data.file) {
      const { subject, level, type, revisionType } = resourceData || {};
      const key = `${subject}/${level}/${type}${
        revisionType ? `/${revisionType}` : ''
      }`;

      const result = await uploadFile('RESOURCE', key, data.file, 'resource');

      resourceData = {
        ...resourceData,
        resourceS3Key: result.key,
        fileType: 'FILE',
      } as ResourcePost;
    }

    return (
      await axios.post<IResource[]>(API_URL + '/resources', resourceData, {
        withCredentials: true,
      })
    ).data;
  },

  getAll: async function (query?: {
    search?: string;
    subject?: string;
    level?: string;
    type?: string;
    revisionType?: string;
    page?: number;
    pageSize?: number;
  }) {
    const params = new URLSearchParams();
    if (query?.search) params.set('search', query.search);
    if (query?.subject) params.set('subject', query.subject);
    if (query?.level) params.set('level', query.level);
    if (query?.type) params.set('type', query.type);
    if (query?.revisionType) params.set('revisionType', query.revisionType);
    if (query?.page) params.set('page', String(query.page));
    if (query?.pageSize) params.set('pageSize', String(query.pageSize));
    const qs = params.toString();
    return (
      await axios.get<IResourcePage>(
        API_URL + `/resources/admin${qs ? `?${qs}` : ''}`,
        { withCredentials: true }
      )
    ).data;
  },

  getAdminLink: async function (id: number) {
    return (
      await axios.get<string>(API_URL + '/resources/admin/' + id + '/link', {
        withCredentials: true,
      })
    ).data;
  },

  getLink: async function (id: number) {
    return (await axios.get<string>(API_URL + '/resources/' + id + '/link'))
      .data;
  },

  delete: async function (id: number) {
    return (await axios.delete<IResource[]>(API_URL + '/resources/' + id, { withCredentials: true })).data;
  },

  update: async function (id: number, data: Partial<IResource>) {
    return (
      await axios.patch<IResource[]>(API_URL + '/resources/' + id, data, {
        withCredentials: true,
      })
    ).data;
  },

  toggleVisibility: async function (id: number, isEnabled: boolean) {
    return (
      await axios.patch<IResource>(
        API_URL + '/resources/' + id + '/visibility',
        { isEnabled },
        { withCredentials: true }
      )
    ).data;
  },

  bulkToggleVisibility: async function (
    updates: { id: number; isEnabled: boolean }[]
  ) {
    return (
      await axios.patch<{ updated: number }>(
        API_URL + '/resources/bulk-visibility',
        { updates },
        { withCredentials: true }
      )
    ).data;
  },
};

export default ResourcesApi;
